import { ActionModal } from '@kcftech/design-system';
import { IProfileChild, IUserProfileUpdateRequest } from '@kcftech/prometheus-client';
import { captureException } from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { DateTimeOptionsPane } from 'components/user_menu/settings_modal/DateTimeOptionsPane';
import { OtherIndicatorOptionsPane } from 'components/user_menu/settings_modal/OtherIndicatorOptionsPane';
import { PersonaOptionsPane } from 'components/user_menu/settings_modal/PersonaOptionsPane';
import { VibrationOptionsPane } from 'components/user_menu/settings_modal/VibrationOptionsPane';
import { useNotificationContext } from 'contexts/NotificationContext';
import { useTheme } from 'contexts/ThemeContext';
import { UserSettings } from 'contexts/user_settings/types';
import { useUserSettingsStore } from 'contexts/user_settings/UserSettingsStore';
import { invalidateBaselineBurstData } from 'custom_hooks/data_hooks/burst_data/useBaselineBurstData';
import { invalidateBurstData } from 'custom_hooks/data_hooks/burst_data/useBurstData';
import { invalidateCurrentUser } from 'custom_hooks/data_hooks/user/useCurrentUserInfo';
import { useProfileSettingsOptions } from 'custom_hooks/data_hooks/user/useProfileSettingsOptions';
import { invalidateFrequencyDomainData } from 'pages/workbench/workbench_tabs/trend/frequency_domain/useFrequencyDomainData';
import { invalidateTimeDomainData } from 'pages/workbench/workbench_tabs/trend/time_domain/useTimeDomainData';
import { invalidateTrendTimeSeriesData } from 'pages/workbench/workbench_tabs/trend/timeseries/useTrendTimeSeriesData';
import { invalidateTrendIndicators } from 'pages/workbench/workbench_tabs/trend/useTrendIndicators';
import { invalidateWaterfallFrequencyData } from 'pages/workbench/workbench_tabs/trend/waterfall/useTrendWaterfallFrequencyData';
import { useForm } from 'react-hook-form';
import { ThemeType } from 'styles/colorMode';
import { queryClient } from 'utils/query';
import { profileResponseToUpdateRequest, updateUserProfile } from 'utils/user';

import { ProfileSection, ProfileSectionsLayout } from '../components';
import { OtherPreferencesPane } from './OtherPreferencesPane';

export type UserProfileModalProps = Readonly<{
    userId: string;
    profile: IProfileChild;
    onClose: () => void;
}>;

const invalidateRelatedQueries = () => {
    invalidateCurrentUser();
    invalidateTrendIndicators();
    invalidateTrendTimeSeriesData();
    invalidateTimeDomainData();
    invalidateFrequencyDomainData();
    invalidateWaterfallFrequencyData();
    invalidateBurstData();
    invalidateBaselineBurstData();
    queryClient.invalidateQueries({
        queryKey: ['indicator-tab-tables'],
    });
};

export type UserSettingsFormData = {
    profile: IUserProfileUpdateRequest;
    settings: UserSettings;
    colorMode: ThemeType;
};

export function UserProfileModal({ userId, profile, onClose }: UserProfileModalProps) {
    const { data: options, isLoading: optionsLoading } = useProfileSettingsOptions();

    const { colorMode, restoreColorMode, setColorMode } = useTheme();

    const { setters: settingsSetters, ...settings } = useUserSettingsStore((state) => ({
        envSwitcher: state.envSwitcher,
        setters: {
            setEnvSwitcherEnabled: state.setEnvSwitcherEnabled,
        },
    }));

    const {
        formState: { isSubmitted, isValid, isValidating, isSubmitting, isDirty },
        handleSubmit,
        reset: formReset,
        setValue,
        control,
    } = useForm<UserSettingsFormData>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',

        defaultValues: {
            settings,
            profile: {
                ...profileResponseToUpdateRequest(profile),
            },
            colorMode,
        },
    });

    const { showToast } = useNotificationContext();

    const saveProfile = useMutation({
        mutationFn: async (updates: Partial<IUserProfileUpdateRequest>) => {
            await updateUserProfile(userId, updates);
        },
        onSuccess: () => {
            invalidateRelatedQueries();
            onClose();
            showToast({
                type: 'success',
                content: 'Settings saved',
            });
        },
        onError: (err) => {
            showToast({
                type: 'error',
                content: 'Failed to update profile',
            });
            captureException(err, {
                tags: { codeLocation: 'UserProfileModal:saveProfile' },
            });
        },
    });

    function reset() {
        formReset();
        // reset any settings previews
        restoreColorMode();
    }

    return (
        <ActionModal
            title="User Profile Settings"
            onAction={handleSubmit((formData) => {
                return Promise.all([
                    saveProfile.mutateAsync(formData.profile),
                    settingsSetters.setEnvSwitcherEnabled(formData.settings.envSwitcher),
                    setColorMode(formData.colorMode),
                ]);
            })}
            actionable={
                isDirty && (!isSubmitted || isValid) && !(isValidating || isSubmitting)
            }
            actionButtonText="Save"
            onClose={() => {
                reset();
                onClose();
            }}
            width={880}
            height={'min(920px, calc(100vh - 1.5rem))'}
        >
            <ProfileSectionsLayout>
                <SpinnerArea isLoading={optionsLoading} noContainer>
                    {options && (
                        <ProfileSection title="Units & Text" className="flex flex-column">
                            <DateTimeOptionsPane
                                options={options}
                                className="date_time_options"
                                control={control}
                            />
                            <VibrationOptionsPane
                                options={options}
                                className="vibration_options"
                                control={control}
                            />
                            <OtherIndicatorOptionsPane
                                options={options}
                                className="other_indicator_options"
                                control={control}
                            />
                            <PersonaOptionsPane
                                options={options}
                                className="persona_options"
                                control={control}
                                setValue={setValue}
                            />
                        </ProfileSection>
                    )}
                </SpinnerArea>
                <OtherPreferencesPane control={control} className="optional_features" />
            </ProfileSectionsLayout>
        </ActionModal>
    );
}
